import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Radio, Loader2, Link2, RefreshCw, ListMusic, AlertTriangle, CheckCircle2, Youtube,
  ArrowUp, ArrowDown, CalendarRange, Save, Eye, EyeOff,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  if (!iso) return null;
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const SeasonSettings = ({ world, onSaved }) => {
  const [from, setFrom] = useState(world.visible_from || '');
  const [until, setUntil] = useState(world.visible_until || '');
  const [alwaysOn, setAlwaysOn] = useState(!!world.always_on);
  const [override, setOverride] = useState(world.manual_override || 'auto');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/radio/worlds/${world.id}/settings`, {
        visible_from: from, visible_until: until, always_on: alwaysOn, manual_override: override,
      }, { withCredentials: true });
      toast.success(`${world.label} visibility saved`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save visibility');
    } finally { setBusy(false); }
  };

  return (
    <details className="mt-3 group" data-testid={`radio-${world.id}-season`}>
      <summary className="cursor-pointer text-[11px] text-gray-400 hover:text-white select-none flex items-center gap-1">
        <CalendarRange className="w-3.5 h-3.5" /> Visibility &amp; season
      </summary>
      <div className="mt-3 grid sm:grid-cols-2 gap-3 bg-black/30 border border-white/5 rounded-xl p-3">
        <label className="text-[11px] text-gray-400">Visible from (IST)
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-9 bg-black/50 border-white/10 text-sm" data-testid={`radio-${world.id}-from`} />
        </label>
        <label className="text-[11px] text-gray-400">Visible until (IST)
          <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="mt-1 h-9 bg-black/50 border-white/10 text-sm" data-testid={`radio-${world.id}-until`} />
        </label>
        <label className="flex items-center justify-between text-[12px] text-gray-300 bg-black/40 rounded-lg px-3 py-2">
          Always on (ignore dates)
          <Switch checked={alwaysOn} onCheckedChange={setAlwaysOn} data-testid={`radio-${world.id}-alwayson`} />
        </label>
        <label className="text-[11px] text-gray-400">Manual override
          <select value={override} onChange={(e) => setOverride(e.target.value)} style={{ colorScheme: 'dark' }} className="mt-1 w-full h-9 bg-black/50 border border-white/10 rounded-md px-2 text-sm text-white" data-testid={`radio-${world.id}-override`}>
            <option value="auto">Auto (follow dates)</option>
            <option value="on">Force ON</option>
            <option value="off">Force OFF</option>
          </select>
        </label>
        <div className="sm:col-span-2 flex items-center justify-between">
          <span className="text-[11px] flex items-center gap-1" data-testid={`radio-${world.id}-visible-now`}>
            {world.visible_now
              ? <><Eye className="w-3.5 h-3.5 text-[#52F0A0]" /> <span className="text-[#52F0A0]">Showing on /radio now</span></>
              : <><EyeOff className="w-3.5 h-3.5 text-gray-500" /> <span className="text-gray-500">Hidden on /radio now</span></>}
          </span>
          <Button onClick={save} disabled={busy} size="sm" className="bg-white/10 hover:bg-white/20 gap-1 h-8" data-testid={`radio-${world.id}-save-season`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </Button>
        </div>
      </div>
    </details>
  );
};

const WorldCard = ({ world, index, total, onSaved, onMove }) => {
  const [url, setUrl] = useState(world.playlist_url || '');
  const [busy, setBusy] = useState(false);
  const linked = !!world.playlist_id;
  const hasTracks = world.has_tracks ?? ((world.tracks || []).length > 0);

  const linkAndSync = async () => {
    if (!url.trim()) { toast.error('Paste a YouTube playlist URL first'); return; }
    setBusy(true);
    try {
      const r = await axios.put(`${API}/radio/worlds/${world.id}/playlist`, { playlist_url: url.trim() }, { withCredentials: true });
      toast.success(`${world.label} synced — ${r.data.count} tracks from the playlist`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not link that playlist');
    } finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/radio/worlds/${world.id}/sync`, {}, { withCredentials: true });
      toast.success(`${world.label} refreshed — ${r.data.count} tracks`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    } finally { setBusy(false); }
  };

  const tracks = world.tracks || [];

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4" data-testid={`radio-world-${world.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="flex flex-col">
            <button disabled={index === 0} onClick={() => onMove(index, -1)} className="text-gray-500 hover:text-white disabled:opacity-20 leading-none" aria-label="Move up" data-testid={`radio-${world.id}-up`}><ArrowUp className="w-3.5 h-3.5" /></button>
            <button disabled={index === total - 1} onClick={() => onMove(index, 1)} className="text-gray-500 hover:text-white disabled:opacity-20 leading-none" aria-label="Move down" data-testid={`radio-${world.id}-down`}><ArrowDown className="w-3.5 h-3.5" /></button>
          </div>
          <span className="text-gray-600 tabular-nums text-sm w-5 text-right">{index + 1}</span>
          <span className="w-8 h-8 rounded-xl bg-[#52F0A0]/15 flex items-center justify-center">
            <Radio className="w-4 h-4 text-[#52F0A0]" />
          </span>
          <div>
            <p className="text-white font-semibold leading-tight">{world.label}</p>
            <p className="text-[11px] text-gray-500">{world.id}{world.seasonal ? ' · seasonal' : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {linked && <Badge className="bg-[#52F0A0]/15 text-[#52F0A0] font-normal gap-1"><ListMusic className="w-3 h-3" />{world.track_count ?? tracks.length} tracks</Badge>}
        </div>
      </div>

      {!hasTracks && (
        <div className="flex items-center gap-2 mb-3 text-[12px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2" data-testid={`radio-${world.id}-no-playlist`}>
          <AlertTriangle className="w-4 h-4 shrink-0" /> No playlist yet — this world stays hidden on /radio until you link a YouTube playlist.
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Youtube className="w-4 h-4 text-red-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Paste YouTube playlist URL (https://youtube.com/playlist?list=…)"
            className="pl-8 h-10 bg-black/50 border-white/10 text-sm"
            data-testid={`radio-${world.id}-playlist-url`}
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={linkAndSync} disabled={busy} size="sm" className="bg-[#52F0A0] text-black hover:bg-[#3dd389] gap-1 h-10" data-testid={`radio-${world.id}-link`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} {linked ? 'Update link' : 'Link & Sync'}
          </Button>
          {linked && (
            <Button onClick={syncNow} disabled={busy} size="sm" variant="ghost" className="text-[#4EC8F5] hover:bg-white/5 gap-1 h-10" data-testid={`radio-${world.id}-sync`}>
              <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> Sync now
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-2 text-[11px]">
        {world.sync_error ? (
          <span className="flex items-center gap-1 text-red-400" data-testid={`radio-${world.id}-error`}>
            <AlertTriangle className="w-3.5 h-3.5" /> {world.sync_error}
          </span>
        ) : linked ? (
          <span className="flex items-center gap-1 text-gray-400">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#52F0A0]" /> Last synced {timeAgo(world.last_synced) || 'never'} · auto-refreshes daily
          </span>
        ) : (
          <span className="text-gray-500">Not linked yet — paste a playlist URL to populate this world.</span>
        )}
      </div>

      <SeasonSettings world={world} onSaved={onSaved} />

      {tracks.length > 0 && (
        <details className="mt-3 group">
          <summary className="cursor-pointer text-[11px] text-gray-400 hover:text-white select-none" data-testid={`radio-${world.id}-preview-toggle`}>
            Preview tracks ({tracks.length})
          </summary>
          <ol className="mt-2 space-y-1 max-h-56 overflow-auto pr-1" data-testid={`radio-${world.id}-tracklist`}>
            {tracks.map((t, i) => (
              <li key={`${t.id}-${i}`} className="flex items-center gap-2 text-xs text-gray-300">
                <span className="text-gray-600 w-5 text-right tabular-nums">{i + 1}.</span>
                <span className="text-white truncate">{t.title || t.id}</span>
                {t.artist && <span className="text-gray-500 truncate">— {t.artist}</span>}
                {t.live && <span className="text-[#4EC8F5]">· live</span>}
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
};

export default function AdminRadioPage() {
  const [worlds, setWorlds] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/radio/admin/worlds`, { withCredentials: true });
      setWorlds(r.data.worlds || []);
    } catch { toast.error('Failed to load radio worlds'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const move = async (index, dir) => {
    const next = [...worlds];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setWorlds(next);  // optimistic
    try {
      await axios.put(`${API}/radio/worlds/reorder`, { order: next.map((w) => w.id) }, { withCredentials: true });
      toast.success('World order updated');
    } catch {
      toast.error('Could not save order');
      load();
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
        <header>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#52F0A0] font-bold">Super Admin</p>
          <h1 className="text-3xl font-extrabold tracking-tight mt-1">Radio Worlds</h1>
          <p className="text-sm text-gray-400 mt-1">
            Link a YouTube playlist to each radio world (the playlist is the single source of truth — edit on YouTube and it flows through, auto-syncs daily or hit <span className="text-[#4EC8F5]">Sync now</span>). Reorder worlds with the arrows, and set seasonal windows under <span className="text-[#4EC8F5]">Visibility &amp; season</span>. A world with no playlist stays hidden on <code className="text-[#4EC8F5]">/radio</code>. Changes go live on <code className="text-[#4EC8F5]">/radio</code>.
          </p>
        </header>

        {loading ? (
          <div className="py-16 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="grid gap-4" data-testid="radio-worlds-list">
            {worlds.map((w, i) => <WorldCard key={w.id} world={w} index={i} total={worlds.length} onSaved={load} onMove={move} />)}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
